import React from 'react';
import { Platform, View } from 'react-native';

import { DebugSqlButton } from '@/components/reports/debug-sql-button';
import { ExportMenu } from '@/components/reports/export-menu';
import { IconButton } from '@/components/ui';

export interface ReportActionBarProps {
	/** Open the column-customizer modal. Omit to hide the Customize button (tree reports). */
	onCustomize?: () => void;
	/** Open a new tab/window with the report's printable view. */
	onPrint: () => void;
	/** Export the report to a `.xlsx`. Disabled when no rows are loaded. */
	onExcel: () => void;
	/** Export the report to a `.csv`. Disabled when no rows are loaded. */
	onCsv: () => void;
	/** Disable export + print while the report is still loading or empty. */
	disabled?: boolean;
	/** Backend API path for the report. Renders a flag-gated Debug SQL button when set. */
	debugSqlPath?: string;
	debugReportTitle?: string;
}

/**
 * Right-side action row for a report page: Debug SQL (flag-gated) · Customize
 * · Print · Export. Export is a dropdown menu (Excel · CSV); Print fires
 * `onPrint`, which the `ReportView` routes to either a definition-specific
 * print URL or the generic `openReportPrintWindow` HTML print fallback. On
 * native the Print icon is hidden since `window.open` isn't available.
 */
export const ReportActionBar: React.FC<ReportActionBarProps> = ({
	onCustomize,
	onPrint,
	onExcel,
	onCsv,
	disabled = false,
	debugSqlPath,
	debugReportTitle,
}) => (
	<View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
		{debugSqlPath ? (
			<DebugSqlButton debugSqlPath={debugSqlPath} reportTitle={debugReportTitle ?? 'Report'} />
		) : null}
		{onCustomize ? (
			<IconButton
				icon="settings-outline"
				size={22}
				onPress={onCustomize}
				disabled={disabled}
				accessibilityLabel="Customize columns"
				testID="report-action-customize"
			/>
		) : null}
		{Platform.OS === 'web' ? (
			<IconButton
				icon="print-outline"
				size={22}
				onPress={onPrint}
				disabled={disabled}
				accessibilityLabel="Print"
				testID="report-action-print"
			/>
		) : null}
		<ExportMenu onExcel={onExcel} onCsv={onCsv} disabled={disabled} />
	</View>
);
